import { subject } from '@casl/ability';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Tooltip } from '@mui/material';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneNumberInput from 'src/components/atoms/phone-number-input/phone-number-input';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { usePrompt } from 'src/hooks/use-prompt';
import { Can } from 'src/permissions/can';
import { z } from 'zod';
import { useUpdateAdminPhoneNumber } from '../../../hooks/api/use-update-admin-phone-number';

interface Props extends DialogActions {
  adminToken: string;
  adminPhoneNumber: string;
}

interface FormValues {
  phoneNumber: string;
}

const UpdateAdminPhoneNumberDialog: FC<Props> = ({
  open,
  onCancel,
  adminToken,
  adminPhoneNumber,
}) => {
  const { t } = useTranslation();

  const validationSchema = z.object({
    phoneNumber: z.string(),
  });

  const defaultValues = {
    phoneNumber: '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  usePrompt(isDirty);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: updateAdminPhoneNumber, isPending: isLoadingUpdateAdmin } =
    useUpdateAdminPhoneNumber(handleOnCancel);

  const onSubmit: SubmitHandler<FormValues> = data => {
    updateAdminPhoneNumber({
      token: adminToken,
      phoneNumber: data.phoneNumber ? `+${data.phoneNumber}` : '',
    });
  };

  useEffect(() => {
    if (open) {
      reset({
        phoneNumber: adminPhoneNumber,
      });
    }
  }, [open, adminPhoneNumber, reset]);

  return (
    <Dialog
      open={open}
      title={t('organizationDetails:admins.dialog.updatePhoneNumber.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(onSubmit)}
      variant="info"
      confirmButtonText={t('common:button.edit')}
      cancelButtonText={t('common:button.cancel')}
      confirmButtonId="invite_administrator_confirm_button"
      isConfirmButtonSubmitting={isLoadingUpdateAdmin}
      isConfirmButtonDisabled={!isValid}>
      <Box sx={{ width: '100%' }}>
        <Can
          I="updatePhoneNumber"
          this={subject('TenantAdministrators', { userToken: adminToken })}
          field={'userToken'}
          passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState,
                  }) => (
                    <PhoneNumberInput
                      label={t('organizationDetails:admins.phoneNumber')}
                      onBlur={onBlur}
                      disabled={!allowed}
                      onChange={onChange}
                      value={value}
                      error={Boolean(
                        fieldState.invalid && fieldState.isTouched,
                      )}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </span>
            </Tooltip>
          )}
        </Can>
      </Box>
    </Dialog>
  );
};

export default UpdateAdminPhoneNumberDialog;
